/**
 * Event-based extension for variant picker functionality
 * Extends existing variant picker behavior without creating new custom elements
 */
export class VariantPickerExtension {
  private picker: HTMLElement;
  private isInitialized: boolean = false;

  constructor(pickerSelector: string = 'variant-picker') {
    this.picker = document.querySelector(pickerSelector) as HTMLElement;
    if (!this.picker) {
      return;
    }
    this.init();
  }

  private init(): void {
    if (this.isInitialized) return;

    // Listen to existing variant picker events
    this.picker.addEventListener('variant:selected', this.handleVariantSelection.bind(this) as EventListener);
    this.picker.addEventListener('variant:changed', this.handleVariantChange.bind(this) as EventListener);

    // Add custom functionality
    this.setupCustomFeatures();

    this.isInitialized = true;
  }

  /**
   * Handle variant selection events from the original picker
   * @param {CustomEvent} event - The variant selection event
   */
  private handleVariantSelection(event: CustomEvent): void {

    // Add your custom logic here
    // For example: analytics tracking, custom UI updates, etc.
    this.trackVariantSelection(event.detail);
    this.updateCustomUI(event.detail);
  }

  /**
   * Handle variant change events from the original picker
   * @param {CustomEvent} event - The variant change event
   */
  private handleVariantChange(event: CustomEvent): void {

    // Add your custom logic here
    this.handleCustomVariantLogic(event.detail);
  }

  /**
   * Setup custom features and UI elements
   */
  private setupCustomFeatures(): void {
    // Add custom data attributes
    this.picker.dataset.variantPickerExtended = 'true';

    // Setup custom UI elements
    this.setupCustomUI();

    // Add custom event listeners to variant options
    this.setupOptionListeners();
  }

  /**
   * Setup custom UI elements
   */
  private setupCustomUI(): void {
    const variantOptions = this.picker.querySelectorAll('input[type="radio"], select option');

    variantOptions.forEach((option: Element) => {
      if (option instanceof HTMLInputElement) {
        option.addEventListener('change', this.handleOptionChange.bind(this));
      }
    });
  }

  /**
   * Setup listeners for variant option changes
   */
  private setupOptionListeners(): void {
    // Listen for changes on radio buttons and selects
    this.picker.addEventListener('change', (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.matches('input[type="radio"], select')) {
        this.handleOptionChange(event);
      }
    });
  }

  /**
 * Handle option change with custom logic
 * @param {Event} event - The change event
 */
  private handleOptionChange(event: Event): void {

    const target = event.target as HTMLInputElement | HTMLSelectElement;

    // Check for subscription data attribute
    if (target.hasAttribute('data-option-subscription')) {
      const subscriptionValue = target.getAttribute('data-option-subscription');
      // Handle subscription-specific logic
      this.handleSubscriptionOption("subscription", target);
    } else {
      this.handleSubscriptionOption(null, target);
    }

    // Add custom pre-processing logic here
    // The original variant picker will still handle the change normally
    this.preProcessVariantChange(event);
  }

  /**
   * Pre-process variant changes
   * @param {Event} event - The change event
   */
  private preProcessVariantChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLSelectElement;

    // Add your custom pre-processing logic here
    // For example: validation, analytics, etc.
  }

  /**
   * Track variant selection for analytics
   * @param {any} variantData - The variant data from the event
   */
  private trackVariantSelection(variantData: any): void {
    // Example analytics tracking

    // You could send to analytics service here
    // gtag('event', 'variant_selected', { variant_id: variantData.id });
  }

  /**
   * Update custom UI based on variant selection
   * @param {any} variantData - The variant data from the event
   */
  private updateCustomUI(variantData: any): void {

    // Add your custom UI update logic here
    // For example: update custom elements, show/hide sections, etc.
  }

  /**
 * Handle custom variant logic
 * @param {any} variantData - The variant data from the event
 */
  private handleCustomVariantLogic(variantData: any): void {
    // Add your custom business logic here
    // For example: subscription logic, inventory checks, etc.
  }

  /**
 * Handle subscription option selection
 * @param {string|null} subscriptionValue - The subscription value from data attribute
 * @param {HTMLElement} target - The selected input element
 */
  private handleSubscriptionOption(subscriptionValue: string | null, target: HTMLElement): void {

    // Toggle product form buttons based on subscription attribute
    this.toggleProductFormButtons(!!subscriptionValue);

    if (subscriptionValue) {
      // Handle different subscription tiers
      switch (subscriptionValue) {
        case 'subscription':
          this.handleSubscriptionSelection(target);
          break;
        default:
          this.handleOneTimeSelection(target);
      }
    }
  }

  /**
   * Handle subscription selection
   * @param {HTMLElement} target - The selected input element
   */
  private handleSubscriptionSelection(target: HTMLElement): void {

    // Add your subscription-specific logic here
    // For example: show subscription benefits, update pricing, etc.

    // Dispatch custom event for other components
    this.dispatchCustomEvent('variant-picker:subscription-selected', {
      element: target,
      subscriptionType: 'subscription'
    });
  }

  /**
 * Handle one-time purchase selection
 * @param {HTMLElement} target - The selected input element
 */
  private handleOneTimeSelection(target: HTMLElement): void {

    // Add your one-time purchase logic here

    // Dispatch custom event for other components
    this.dispatchCustomEvent('variant-picker:one-time-selected', {
      element: target,
      subscriptionType: 'one-time'
    });
  }

  /**
   * Toggle product form buttons visibility based on subscription attribute
   * @param {boolean} hasSubscription - Whether the selected option has subscription attribute
   */
  private toggleProductFormButtons(hasSubscription: boolean): void {
    const productFormButtons = document.querySelector('.product-form-buttons') as HTMLElement;
    if (productFormButtons) {
      if (hasSubscription) {
        productFormButtons.style.display = 'none';

        // Scroll to subscription tiers with 100px offset
        const subscriptionTierElement = document.querySelector('.section-subscription-tiers');
        if (subscriptionTierElement) {
          const elementPosition = subscriptionTierElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }

      } else {
        productFormButtons.style.display = '';
      }
    }
  }

  /**
   * Dispatch custom events for external listeners
   * @param {string} eventName - The event name
   * @param {any} detail - The event detail
   */
  private dispatchCustomEvent(eventName: string, detail: any): void {
    this.picker.dispatchEvent(new CustomEvent(eventName, {
      detail,
      bubbles: true
    }));
  }

  /**
   * Public method to manually trigger custom functionality
   * @param {string} action - The action to perform
   * @param {any} data - Additional data
   */
  public triggerCustomAction(action: string, data?: any): void {
    console.log('VariantPickerExtension: Triggering custom action', action, data);
    switch (action) {
      case 'refresh':
        this.setupCustomFeatures();
        break;
      case 'custom-event':
        this.dispatchCustomEvent('variant-picker:custom-action', { action, data });
        break;
      default:
    }
  }
}

// Initialize the extension when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize for all variant pickers on the page
  const variantPickers = document.querySelectorAll('variant-picker');
  variantPickers.forEach((picker, index) => {
    new VariantPickerExtension(`variant-picker:nth-of-type(${index + 1})`);
  });

  // Or initialize for a specific picker
  // new VariantPickerExtension('#my-specific-variant-picker');
});

// Export for manual initialization if needed




